import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { NotInterested as IrrelevantIcon } from '@mui/icons-material';
import { Finding } from '../../../types';

interface IrrelevantReasonDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (finding: Finding, reason: string) => void;
  finding: Finding | null;
}

const IrrelevantReasonDialog: React.FC<IrrelevantReasonDialogProps> = ({
  open,
  onClose,
  onConfirm,
  finding,
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (finding && reason.trim()) {
      onConfirm(finding, reason.trim());
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && reason.trim()) {
      e.preventDefault();
      handleConfirm();
    }
  };

  if (!finding) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <IrrelevantIcon color="action" />
        <Typography variant="h6" component="span">
          Mark as Irrelevant
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Finding:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {finding.title}
          </Typography>
        </Box>

        <TextField
          autoFocus
          fullWidth
          multiline
          rows={3}
          label="Why is this finding irrelevant?"
          placeholder="Provide a reason..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={handleKeyPress}
          variant="outlined"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!reason.trim()}
        >
          Mark Irrelevant
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IrrelevantReasonDialog;
